import { isSelected } from "./toggleOption";

interface IArgs {
  optionKey: string;
  /** Attribute combinations that do not exist at all — those options are hidden. */
  unAvailabeKeys?: string[];
  /** Existing variants with no sellable quantity left — those stay visible, but marked. */
  outOfStockKeys?: string[];
  /**
   * Variants disabled by the backend (`is_disable`) — same out-of-stock look, but
   * never selectable, not even for the stock reminder flow.
   */
  disabledKeys?: string[];
  /**
   * Tenant setting: an out-of-stock variant stays selectable so the shopper can ask
   * to be reminded once it is restocked.
   */
  allowRequestStockReminder?: boolean;
}

export interface IOptionAvailability {
  /** The variant exists but is sold out — always render the out-of-stock treatment. */
  outOfStock: boolean;
  /** The option cannot be picked at all. */
  disabled: boolean;
  /** Sold out, yet still selectable (stock reminder flow). */
  allowSelectOutOfStock: boolean;
  /** Disabled by the backend (`is_disable`) — label it "not available", not sold out. */
  isDisabled: boolean;
}

/**
 * Single source of truth for how one attribute/filter option maps to its
 * selectable + out-of-stock state, so every option renderer behaves the same.
 */
export const getOptionAvailability = ({
  optionKey,
  unAvailabeKeys,
  outOfStockKeys,
  disabledKeys,
  allowRequestStockReminder,
}: IArgs): IOptionAvailability => {
  const unavailable = isSelected(unAvailabeKeys, optionKey);
  const outOfStock = isSelected(outOfStockKeys, optionKey);
  const backendDisabled = isSelected(disabledKeys, optionKey);

  if (backendDisabled)
    return {
      outOfStock: true,
      disabled: true,
      allowSelectOutOfStock: false,
      isDisabled: true,
    };

  return {
    outOfStock,
    disabled: unavailable || (outOfStock && !allowRequestStockReminder),
    allowSelectOutOfStock: outOfStock && !!allowRequestStockReminder,
    isDisabled: false,
  };
};
